import { ChevronDown, ChevronRight, Users } from 'lucide-react';
import React, { useState } from 'react';

import { useGetFirmsQuery } from '@/services/api/firmsService';
import { Firm } from '@/services/types/firms-types';

import { Button } from '../../ui/button';
import {
  Combobox,
  ComboboxLabel,
  ComboboxOption,
} from '../../ui/catalyst/combobox';
import { AssignmentDropdown } from './AssignmentDropdown';

interface IntakeAssignmentPanelProps {
  selectedFirm: { id: string; name: string } | null;
  onFirmChange: (firm: { id: string; name: string } | null) => void;
  assignedTo: string | null;
  onAssignedToChange: (userId: string | null) => void;
  disabled?: boolean;
}

const IntakeAssignmentPanelComponent: React.FC<IntakeAssignmentPanelProps> = ({
  selectedFirm,
  onFirmChange,
  assignedTo,
  onAssignedToChange,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { data: firms = [], isLoading: firmsLoading } = useGetFirmsQuery({});

  // Transform firms for dropdown
  const firmOptions = firms.map((firm: Firm) => ({
    id: firm.id,
    name: firm.name,
  }));

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <Button
        variant="ghost"
        onClick={handleToggle}
        className="w-full justify-between p-3 text-left hover:bg-gray-50"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Assignment</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </Button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          <div className="space-y-2.5 pt-2.5">
            {/* Firm Selection */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-gray-700">Firm</div>
              <Combobox
                value={selectedFirm ?? undefined}
                onChange={(firm) => onFirmChange(firm)}
                options={firmOptions}
                displayValue={(firm: { id: string; name: string } | null) =>
                  firm?.name || ''
                }
                placeholder=""
                className="w-full text-sm [&>span>input]:py-1.5 [&>span>input]:text-sm"
                aria-label="Select firm"
                disabled={disabled || firmsLoading}
              >
                {(option: { id: string; name: string }) => (
                  <ComboboxOption value={option}>
                    <ComboboxLabel>{option.name}</ComboboxLabel>
                  </ComboboxOption>
                )}
              </Combobox>
              {firmsLoading && (
                <p className="text-xs text-gray-400">Loading firms...</p>
              )}
            </div>

            {/* Assignment Section */}
            <div>
              <AssignmentDropdown
                value={assignedTo}
                onChange={onAssignedToChange}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const IntakeAssignmentPanel = React.memo(IntakeAssignmentPanelComponent);
